import { nanoid } from 'nanoid';

import { _getLogger } from '@doxyme/analytics';
import { Specs, type CapabilityConfig } from '@doxyme/extensions-common';
import { type Manifest } from '@doxyme/extensions-graphql-types';
import type Bridge from './bridge';
import type Runtime from './runtimes/runtime';
import { logErrorAndThrowBind, type LogErrorAndThrowBound } from './utils';

const logger = _getLogger('instance', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

/**
 * The source that loaded the extension instance.
 * - `registry`: Loaded from the backend registry (default)
 * - `sideload`: Loaded via URL parameters for development
 */
export type InstanceSource = 'registry' | 'sideload';

/**
 * The Extension Instance is responsible for managing the extension's runtime(s), notifying runtimes of events, and handling actions from the runtimes that it passes up to the Bridge to be forwarded to the appropriate Capabilities.
 */
export default class ExtensionInstance<
  Constraints extends Specs.Constraints = Specs.Constraints,
> {
  /**
   * The ID as defined in the extension manifest
   */
  readonly id: string;

  /**
   * The manifest definition for the extension
   */
  readonly manifest: Manifest;

  /**
   * The source that loaded this instance
   */
  readonly source: InstanceSource;

  /**
   * The list of capability configs for the extension
   */
  readonly configs: CapabilityConfig[] = [];

  readonly #bridge: Bridge;

  #runtimes: Runtime[] = [];

  /**
   * Create a new Extension Instance
   * @param bridge - A reference to the bridge
   * @param manifest - The extension's manifest
   * @param source - The source that loaded this instance (defaults to 'registry')
   */
  constructor(
    bridge: Bridge,
    manifest: Manifest,
    source: InstanceSource = 'registry',
  ) {
    this.id = manifest.id;
    this.#bridge = bridge;
    this.manifest = manifest;
    this.source = source;

    for (const capability of manifest.capabilities as CapabilityConfig[]) {
      const config = {
        ...capability,
        // This id is defined here for internal use, is not part of manifest and can not be overridden
        id: `${capability.type}/${nanoid(6)}`,
      };
      this.configs.push(config);
    }
  }

  getBridge(): Bridge {
    return this.#bridge;
  }

  /**
   * Get all capability configs for a given extension that match a particular type
   * @param type - The capability type to match against
   * @example
   * ```ts
   * const configs = instance.configsBy('receive-context');
   * ```
   */
  configsBy(type: string): CapabilityConfig[] {
    return this.configs.filter((c) => c.type === type);
  }

  /**
   * Check if an Extension uses a given Capability type
   * @param type - The capability type to check against
   * @returns - Boolean indicating whether the extension uses that capability type
   */
  usesCapability(type: string): boolean {
    return this.configs.some((c) => c.type === type);
  }

  /**
   * @hidden
   */
  addRuntime(runtime: Runtime): void {
    runtime.setInstance(this);
    this.#runtimes.push(runtime);
  }

  /**
   * @hidden
   */
  removeRuntime(id: string): void {
    this.#runtimes = this.#runtimes.filter((r) => r.id !== id);
  }

  /**
   * @hidden
   */
  notify(tuple: Constraints['eventPayloadTuple']): void {
    const [type] = tuple;
    const spec = Specs.byEventType(type);

    if (!spec) {
      logErrorAndThrow(`[notify] Unknown event type: ${type}`);
    }

    if (!this.usesCapability(spec.id) && spec.id !== Specs.System.id) {
      logger.warn(`Rejecting event for unregistered capability: ${type}`);
      return;
    }

    for (const runtime of this.#runtimes) {
      runtime.notify(tuple);
    }
  }

  /**
   * @hidden
   */
  receive(tuple: Constraints['actionPayloadTuple']): void {
    const type = tuple[0];
    const spec = Specs.byAction(type);

    if (!this.usesCapability(spec.id)) {
      logger.warn(`Rejecting action for unregistered capability: ${type}`);
      return;
    }

    this.#bridge.receive(this, tuple);
  }
}
